import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList, Cell } from 'recharts';

// Barras horizontales para las agrupaciones del backend ({nombre, cantidad,
// porcentaje}, de mayor a menor). Horizontales porque los nombres (cursos,
// procedencias) son largos y en vertical no se leen.
//
// Con datos reales hay hasta ~200 procedencias o ~650 cursos: se muestran las
// `maximo` con más personas y el resto se suma en una barra "Otras (N)", así la
// gráfica no mide metros y la suma sigue coincidiendo con el total.
const agruparResto = (filas, maximo, campo) => {
  if (filas.length <= maximo) return filas;
  const visibles = filas.slice(0, maximo);
  const resto = filas.slice(maximo);
  const suma = (campo) => resto.reduce((acc, f) => acc + Number(f[campo] || 0), 0);
  return [
    ...visibles,
    {
      nombre: `Otras (${resto.length})`,
      [campo]: suma(campo),
      ...(campo !== 'cantidad' ? { cantidad: suma('cantidad') } : {}),
      porcentaje: Math.round(suma('porcentaje') * 100) / 100,
      esResto: true,
    },
  ];
};

// Por defecto cuenta personas (`cantidad`). Para otras magnitudes (p. ej. el
// monto recaudado por centro en Reporte de Pagos) se pasa `campo`, `formato`,
// `tooltip` y `queSeCuenta`.
const tooltipPersonas = (valor, item) => [`${valor} personas (${item?.payload?.porcentaje ?? 0}%)`, 'Inscritos'];

const GraficaBarras = ({
  titulo, datos, nota, color = '#8884d8', alturaMinima = 260, maximo = 15,
  campo = 'cantidad', formato = (v) => v, tooltip = tooltipPersonas, queSeCuenta = 'personas', nombreSerie = 'Inscritos',
}) => {
  const todas = (Array.isArray(datos) ? datos : []).map((f) => ({ ...f, nombre: String(f.nombre ?? '') }));
  const filas = agruparResto(todas, maximo, campo);
  const alto = Math.max(alturaMinima, filas.length * 34 + 60);
  return (
    <div className="chart-box" style={{ width: '100%' }}>
      <h4 style={{ fontSize: '1.2rem' }}>{titulo}</h4>
      {nota && <div className="small text-muted mb-2">{nota}</div>}
      {todas.length > maximo && (
        <div className="small text-muted mb-2">
          Se muestran las {maximo} con más {queSeCuenta}; las otras {todas.length - maximo} se suman en "Otras".
        </div>
      )}
      {filas.length === 0 ? (
        <div className="text-muted small py-4">Sin datos para los filtros aplicados.</div>
      ) : (
        <ResponsiveContainer width="100%" height={alto}>
          <BarChart data={filas} layout="vertical" margin={{ top: 5, right: 50, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" allowDecimals={false} tickFormatter={formato} />
            <YAxis type="category" dataKey="nombre" width={200} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(valor, _n, item) => tooltip(valor, item)} />
            <Bar dataKey={campo} name={nombreSerie} fill={color}>
              {/* "Otras" en gris: no es una categoría, es la suma del resto. */}
              {filas.map((f, i) => <Cell key={i} fill={f.esResto ? '#b0b0b0' : color} />)}
              <LabelList dataKey={campo} position="right" formatter={formato} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default GraficaBarras;
